import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";

import {
  apiRequest,
  formatCurrency,
  formatDate,
  getProfileImage,
} from "../../../core/common/api";
import AppImage from "../../../shared/AppImage";
import MetricCard from "../../../shared/MetricCard";
import PaginationBar from "../../../shared/PaginationBar";
import {
  creditTypeLabel,
  creditStatusLabel,
  toneForCreditStatus,
  toneForCreditType,
} from "../../../shared/Utils";

const amountClass = (type = "") => {
  if (type === "credit_used") return "credit-amount-used";
  if (type === "payment_received" || type === "credit_reversed")
    return "credit-amount-paid";
  return "";
};

const CreditHistory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const scopedUserId = searchParams.get("userId") || "";

  const [transactions, setTransactions] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalTransactions: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    type: "all",
    status: "all",
  });
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(filters.search), 400);
    return () => window.clearTimeout(timer);
  }, [filters.search]);

  const loadHistory = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(pagination.limit),
        });

        if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
        if (filters.type !== "all") params.set("type", filters.type);
        if (filters.status !== "all") params.set("status", filters.status);
        if (scopedUserId) params.set("userId", scopedUserId);

        const result = await apiRequest(`/credit/history?${params}`, "GET");
        setTransactions(result.transactions || []);
        setStats(result.stats || {});
        if (result.pagination) {
          setPagination({
            page: result.pagination.page,
            limit: result.pagination.limit,
            totalPages: result.pagination.totalPages,
            totalTransactions: result.pagination.totalTransactions || 0,
          });
        }
      } catch (error: any) {
        Swal.fire("Error", error.message || "Unable to load credit history", "error");
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, filters.status, filters.type, pagination.limit, scopedUserId],
  );

  useEffect(() => {
    void loadHistory(1);
  }, [loadHistory]);

  const scopedUser = useMemo(() => {
    if (!scopedUserId) return null;
    return transactions.find((item) => item.user?._id === scopedUserId)?.user || null;
  }, [scopedUserId, transactions]);

  const scopedLabel = scopedUser?.name || "Selected customer";
  const columnCount = scopedUserId ? 8 : 9;
  const hasActiveFilters =
    Boolean(filters.search.trim()) ||
    filters.type !== "all" ||
    filters.status !== "all" ||
    Boolean(scopedUserId);

  const clearUserScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("userId");
    setSearchParams(next);
  };

  const resetFilters = () => {
    setFilters({ search: "", type: "all", status: "all" });
    if (scopedUserId) clearUserScope();
  };

  return (
    <div className="credit-history-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Finance</div>
          <h1 className="page-title">
            Credit <em>History</em>
          </h1>
          <div className="page-sub">
            {scopedUserId
              ? `All history for ${scopedLabel}`
              : "Limit − Due = Available"}
          </div>
        </div>
      </div>

      {scopedUserId && (
        <div className="credit-scope-bar">
          <div className="credit-scope-user">
            {scopedUser ? (
              <Link to={`/users/${scopedUserId}`} className="img-name-cell">
                <AppImage
                  src={getProfileImage(scopedUser)}
                  alt={scopedUser.name || "Customer"}
                  crossOrigin="anonymous"
                />
                <div>
                  <strong>{scopedUser.name || "Customer"}</strong>
                  <small>{scopedUser.email || scopedUser.phone || "—"}</small>
                </div>
              </Link>
            ) : (
              <strong>{scopedLabel}</strong>
            )}
          </div>
          <div className="hdr-actions">
            <Link to={`/users/${scopedUserId}`} className="btn-sm btn-outline">
              View Customer
            </Link>
            <button type="button" className="btn-sm btn-outline" onClick={clearUserScope}>
              Show All
            </button>
          </div>
        </div>
      )}

      <section className="metrics-grid cols-3">
        {[
          { label: "Credit Limit", value: formatCurrency(stats.totalCreditLimit), icon: "💳", tone: "blue" },
          { label: "Used / Due", value: formatCurrency(stats.totalOutstanding), icon: "⏳", tone: "orange" },
          { label: "Available Credit", value: formatCurrency(stats.availableCredit), icon: "✅", tone: "green" },
        ].map((metric) => (
          <MetricCard key={metric.label} metric={metric} />
        ))}
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={filters.search}
            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
            placeholder={
              scopedUserId
                ? "Search within this customer"
                : "Search customer name, email or phone"
            }
          />
        </div>
        <select
          className="filter-select"
          value={filters.type}
          onChange={(event) => setFilters((current) => ({ ...current, type: event.target.value }))}
        >
          <option value="all">All Activity</option>
          <option value="limit_set">Limit Updated</option>
          <option value="credit_used">Credit Used</option>
          <option value="payment_received">Payment Received</option>
          <option value="credit_reversed">Credit Reversed</option>
        </select>
        <select
          className="filter-select"
          value={filters.status}
          onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
        >
          <option value="all">All Status</option>
          <option value="outstanding">Due (Unpaid)</option>
          <option value="partially_settled">Partially Paid</option>
          <option value="settled">Fully Paid</option>
          <option value="reversed">Reversed</option>
        </select>
        <div className="toolbar-right">
          <button
            type="button"
            className="btn-sm btn-outline"
            onClick={resetFilters}
            disabled={!hasActiveFilters}
          >
            Reset
          </button>
        </div>
      </div>

      <section className="table-card">
        <div className="table-head table-head-flex">
          <div>
            <h2>All Entries</h2>
            <p>
              {pagination.totalTransactions} total entries
              {scopedUserId ? " for this customer" : ""}
            </p>
          </div>
        </div>
        <div className="table-scroll">
          <table className={`credit-history-table${scopedUserId ? " is-scoped" : ""}`}>
            <thead>
              <tr>
                {!scopedUserId && <th>Customer</th>}
                <th>Activity</th>
                <th>Order</th>
                <th>Amount</th>
                <th>Due</th>
                <th>Status</th>
                <th>Reference</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columnCount} className="empty-state">
                    Loading credit history...
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="empty-state">
                    No credit activity found.
                  </td>
                </tr>
              ) : (
                transactions.map((transaction) => {
                  const reference = transaction.paymentReference || transaction.note || "";
                  return (
                    <tr key={transaction._id}>
                      {!scopedUserId && (
                        <td>
                          <Link to={`/users/${transaction.user?._id}`} className="img-name-cell">
                            <AppImage
                              src={getProfileImage(transaction.user)}
                              alt={transaction.user?.name || "Customer"}
                              crossOrigin="anonymous"
                            />
                            <div>
                              <strong>{transaction.user?.name || "Customer"}</strong>
                              <small>{transaction.user?.email || transaction.user?.phone || "—"}</small>
                            </div>
                          </Link>
                        </td>
                      )}
                      <td>
                        <span className={`status-badge ${toneForCreditType(transaction.type)}`}>
                          {creditTypeLabel(transaction.type)}
                        </span>
                      </td>
                      <td>
                        {transaction.order?._id ? (
                          <Link to={`/orders/${transaction.order._id}`} className="order-code">
                            {transaction.order.orderCode}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <strong className={amountClass(transaction.type)}>
                          {formatCurrency(transaction.amount)}
                        </strong>
                      </td>
                      <td>{formatCurrency(transaction.balanceAfter)}</td>
                      <td>
                        <span className={`status-badge ${toneForCreditStatus(transaction.status)}`}>
                          {creditStatusLabel(transaction.status)}
                        </span>
                      </td>
                      <td>
                        <span className="credit-ref-cell" title={reference || undefined}>
                          {reference || "—"}
                        </span>
                      </td>
                      <td>{formatDate(transaction.createdAt)}</td>
                      <td>
                        <Link
                          to={`/credit-history/${transaction._id}`}
                          className="btn-sm btn-outline"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.totalTransactions}
          totalPages={pagination.totalPages}
          itemLabel="entries"
          onPageChange={loadHistory}
          onLimitChange={(limit) =>
            setPagination((current) => ({ ...current, limit, page: 1 }))
          }
        />
      </section>
    </div>
  );
};

export default CreditHistory;
